import { Component, Inject, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup, NgForm, UntypedFormBuilder } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { formatAddress } from 'app/helpers/stringHelpers';
import { ExposedCreateMortgageEligibilityInfoPropertiesReq } from 'app/interfaces/requests/Exposed/exposedCreateMortgageEligibilityInfoPropertiesReq';
import { ExposedListSearchAddressRes } from 'app/interfaces/responses/Exposed/exposedListSearchAddressRes';
import { Option } from 'app/interfaces/support/option';
import { MortgageEligibilityInfoService } from 'app/services/api/mortgageEligibilityInfo/mortgageEligibilitInfo.service';
import { catchError, debounceTime, of, switchMap } from 'rxjs';
import { DialogGeneral } from '../../../DialogGeneral';

export interface DialogMortgageEligibilityAddPropertyData {
    mortgageEligibilityInfoId: number;
}

@Component({
    selector: 'dialog-mortgage-eligibility-add-property',
    templateUrl: 'dialog-mortgage-eligibility-add-property.html',
    encapsulation: ViewEncapsulation.None,
})
export class DialogMortgageEligibilityAddPropertyComponent extends DialogGeneral {
    @ViewChild('ngForm') ngForm: NgForm;
    form: FormGroup;
    formEnabled: boolean;
    loadingAddresses: boolean = false;
    addressSearchResults: Option<ExposedListSearchAddressRes>[] = [];
    selectedProperty: ExposedListSearchAddressRes;

    /**
     * Constructor
     */
    constructor(
        public matDialogRef: MatDialogRef<DialogMortgageEligibilityAddPropertyComponent>,
        private formBuilder: UntypedFormBuilder,
        private mortgageEligibilityInfoService: MortgageEligibilityInfoService,
        public dialogRef: MatDialogRef<null>,
        public dialog: MatDialog,
        @Inject(MAT_DIALOG_DATA)
        public data: DialogMortgageEligibilityAddPropertyData
    ) {
        super(dialogRef);
        this.initForm();
    }

    initForm = () => {
        this.form = this.formBuilder.group({
            addressSearch: new FormControl(''),
        });

        this.formEnabled = true;
        this.initSearchAddress();
    };

    initSearchAddress(): void {
        let control = this.form.get('addressSearch');
        control.valueChanges
            .pipe(
                debounceTime(300),
                switchMap((query: string) => {
                    if (query != '' && this.formEnabled) {
                        this.loadingAddresses = true;
                        this.addressSearchResults = [];
                        return this.mortgageEligibilityInfoService.searchAddress(query).pipe(
                            catchError(() => {
                                return of(null);
                            })
                        );
                    }
                })
            )
            .subscribe({
                next: (results: ExposedListSearchAddressRes[]) => {
                    this.loadingAddresses = false;
                    this.addressSearchResults = results.map((item) => {
                        return {
                            value: item,
                            label: `${formatAddress(item.name, item.neighborhoodName, item.postalCode)} (F${item.propertyNumber})`,
                        } as Option<ExposedListSearchAddressRes>;
                    });
                },
                error: (err) => {
                    this.loadingAddresses = false;
                },
            });
    }

    onPropertySelected = (value: ExposedListSearchAddressRes) => {
        this.selectedProperty = value;
        this.form
            .get('addressSearch')
            .setValue(`${formatAddress(value.name, value.neighborhoodName, value.postalCode)} (F${value.propertyNumber})`, {
                emitEvent: false,
                onlySelf: true,
            });
        this.addressSearchResults = [];
    };

    submit = () => {
        if (!this.form.valid) {
            return;
        }

        this.formEnabled = false;
        this.form.disable();

        let req = {
            mortgageEligibilityInfoId: this.data.mortgageEligibilityInfoId,
            propertyNumber: 'F' + this.selectedProperty.propertyNumber,
            address: this.selectedProperty.name,
            city: this.selectedProperty.neighborhoodName,
            postalCode: this.selectedProperty.postalCode,
        } as ExposedCreateMortgageEligibilityInfoPropertiesReq;

        this.mortgageEligibilityInfoService.createMortgageEligibilityInfoProperty(req).subscribe((id: number) => {
            if (id) {
                this.dialogRef.close(id);
            } else {
                this.form.enable();
            }
            this.formEnabled = true;
        });
    };
}
